<script setup lang="ts">
    import { onMounted, reactive, watch, ref } from 'vue';
    import { getOrderedLanguageOptions, type AiDropdownOption, LanguageOptionParameters } from '@cwd/AiTools/AiEditor.access';
    import Modal from '@shared/components/Modal.vue';
    import { setupShared } from '@cwd/AiTools/Shared';
    import Terms from '@cwd/AiTools/Terms.vue';
    import BSFormInputTextarea from '@shared/components/BSFormInputTextarea.vue';
    import LanguageOptionDropdownWithFilter from '@shared/components/LanguageOptionDropdownWithFilter.vue';
    import SurveyTypeSelectorWithNew from './SurveyTypeSelectorWithNew.vue';
    import BsAlert from '@shared/components/BsAlert.vue';
    import Loading from '@shared/components/Loading.vue';
    import { Language } from '@shared/Root/Languages.access';
    import { AlertTypes } from '@shared/components/BsAlerts';

    const { shared, acceptTerms, dismissAIDisclaimer, showConsumption, getFormattedConsumption, updateConsumption, makeCreateSurveyRequest } = setupShared();

    const props = defineProps<{
        createSurvey: {
            show: boolean;
            selectedSurveyId: string;
            surveyType: string;
            language: string;
            culture: string;
        };
    }>();

    const emit = defineEmits(['close', 'update:createSurvey']);
    const isSurveyTypeSelected = ref<boolean>(false);
    const questionCountShort = 6;
    const questionCountMedium = 12;
    const questionCountLong = 20;
    const defaultQuestionCount = questionCountMedium;
    const selectedQuestionCount = ref(defaultQuestionCount);
    const ratingScoreLimitForDescription = 10;

    function getRatingScaleInstructions(max: number): string {
        let instruction = '';
        if (max >= ratingScoreLimitForDescription) {
            instruction = 'Only add numbering to the rating scale.';
        } else {
            instruction = 'Add description to first and last rating score and numbering on the other scores.';
        }
        instruction += 'Always keep the scores evenly spaced.';
        instruction += `At bottom of Survey Introduction, add: <span><br><br><em>${$Resources.SurveyQuestionRatingScoreLegend}</em></span>`;
        return instruction;
    }

    const minRatingScale = 3;
    const maxRatingScale = 10;
    const defaultRatingScaleMax = 5;
    const numberOfRatingScores = ref(defaultRatingScaleMax);
    const ratingScaleOptions = Array.from({ length: maxRatingScale - minRatingScale + 1 }, (_, i) => i + minRatingScale);
    const isOnePage = ref(false);

    let languageOptions: AiDropdownOption[] = [];
    try {
        languageOptions = await getOrderedLanguageOptions();
    } catch (error) {
        console.warn(`Failed to load language options: ${error}. Using fallback languages.`);
        languageOptions = addFallbackOptions();
    }

    const visibleLanguageOptionCount = 10;

    const defaultLanguage: LanguageOptionParameters = {
        language: 'Swedish',
        culture: 'sv-SE',
    };
    const selectedLanguage = ref<LanguageOptionParameters>(defaultLanguage);

    const state = reactive<{
        prompt: string;
        extraInfo: string;
        params: any;
    }>({
        prompt: '',
        extraInfo: '',
        params: {},
    });

    // Initialize isSurveyTypeSelected based on the prompt value
    watch(
        () => state.prompt,
        (newVal) => {
            // If prompt has a value != 'custom-survey-type-value', then a survey type is selected
            // (Fix for when user happens to enter the value for the custom survey type option - set it to something no one would enter.)
            isSurveyTypeSelected.value = Boolean(newVal && newVal.trim() !== '' && newVal !== 'custom-survey-type-value');
        },
        { immediate: true }
    );

    onMounted(async () => {
        selectedQuestionCount.value = defaultQuestionCount;
        numberOfRatingScores.value = defaultRatingScaleMax;
        isOnePage.value = false;
        selectedLanguage.value = defaultLanguage;

        watch(
            () => props.createSurvey.show,
            (newValue) => {
                shared.show = newValue;
                if (newValue === true) init();
            },
            { immediate: true }
        );

        // Add a watch to sync language values from props when they change
        watch(
            () => [props.createSurvey.language, props.createSurvey.culture],
            ([newLanguage, newCulture]) => {
                if (newLanguage && newCulture) {
                    selectedLanguage.value = {
                        language: newLanguage,
                        culture: newCulture,
                    };
                }
            }
        );
    });

    const init = async () => {
        await updateConsumption();
        // Reset selection state according to current prompt value
        isSurveyTypeSelected.value = Boolean(state.prompt && state.prompt.trim() !== '' && state.prompt !== 'custom-survey-type-value');
    };

    const create = () => {
        state.params = {
            type: state.prompt,
            surveyId: props.createSurvey.selectedSurveyId,
            surveyType: props.createSurvey.surveyType,
            questionCount: selectedQuestionCount.value,
            numberOfRatingScores: numberOfRatingScores.value,
            ratingScoresDescription: getRatingScaleInstructions(numberOfRatingScores.value),
            isOnePage: isOnePage.value,
            language: selectedLanguage.value.language,
            culture: selectedLanguage.value.culture,
        };

        makeCreateSurveyRequest('create-survey', state.extraInfo, state.params);
    };

    const selectQuestionCount = (count: number) => {
        selectedQuestionCount.value = count;
        emit('update:createSurvey', { ...props.createSurvey, questionCount: count });
    };

    const selectNumberOfRatingScores = (max: number) => {
        numberOfRatingScores.value = max;
        emit('update:createSurvey', { ...props.createSurvey, numberOfRatingScores: max });
    };

    const togglePageLayout = (newValue: boolean) => {
        isOnePage.value = newValue;
        emit('update:createSurvey', { ...props.createSurvey, isOnePage: newValue });
    };

    const handleLanguageSelected = (languageOption: LanguageOptionParameters) => {
        selectedLanguage.value = languageOption;
        emit('update:createSurvey', {
            ...props.createSurvey,
            language: languageOption.language,
            culture: languageOption.culture,
        });
    };

    function addFallbackOptions(): AiDropdownOption[] {
        console.warn('Using only supported language options as fallback.');
        const fallbackOptions: Language[] = [
            { LanguageId: 'sv', Name: 'Swedish', Culture: 'sv-SE' },
            { LanguageId: 'en', Name: 'English', Culture: 'en-US' },
            { LanguageId: 'no', Name: 'Norwegian', Culture: 'no-NO' },
            { LanguageId: 'da', Name: 'Danish', Culture: 'da-DK' },
            { LanguageId: 'fi', Name: 'Finnish', Culture: 'fi-FI' },
            { LanguageId: 'fr', Name: 'French', Culture: 'fr-FR' },
        ];

        const fallbackLanguageOptions = fallbackOptions.map((language) => ({
            Command: 'translate' as const,
            Parameters: {
                language: language.Name,
                culture: language.Culture,
            },
            Text: language.Name,
        }));
        return fallbackLanguageOptions;
    }
</script>

<template>
    <Modal
        :class="{ 'ai-loading': shared.loading }"
        :title="$Resources.AiAssistantCreateSurveyTitle"
        :beta="false"
        icon="ai"
        :show="shared.show"
        size="md"
        @close="$emit('close')"
        :scrollBody="false">
        <template #content>
            <div>
                <Terms :termsAccepted="shared.termsAccepted" @accept="acceptTerms()"></Terms>

                <SurveyTypeSelectorWithNew v-model="state.prompt" @surveyTypeSelected="isSurveyTypeSelected = true" />

                <!-- Free-text prompt -->
                <BSFormInputTextarea
                    class="form-group"
                    :modelValue="state.extraInfo"
                    @update:modelValue="(newValue: string) => (state.extraInfo = newValue)"
                    :required="true"
                    :label="$Resources.AIAssistantCreateSurveyExtraInfo"
                    :infoText="$Resources.AIAssistantCreateSurveyExtraInfoHelp"
                    :placeholder="$Resources.AIAssistantCreateSurveyExtraInfoPlaceholder"></BSFormInputTextarea>

                <div class="form-group">
                    <LanguageOptionDropdownWithFilter
                        :label="$Resources.AIAssistantCreateSurveyLanguageOptionTitle"
                        :languageOptions="languageOptions"
                        :visibleLanguageOptionCount="visibleLanguageOptionCount"
                        :defaultLanguageOption="{ language: selectedLanguage.language, culture: selectedLanguage.culture }"
                        :modelValue="{ language: selectedLanguage.language, culture: selectedLanguage.culture }"
                        @update:modelValue="handleLanguageSelected" />
                </div>

                <!-- Survey Length Selector -->
                <div class="form-group survey-length-selector">
                    <h3 class="label-look">{{ $Resources.AIAssistantCreateSurveyLengthOptionTitle + ':' }}</h3>

                    <div class="btn-group">
                        <button
                            type="button"
                            :class="['btn', 'btn-sm', selectedQuestionCount === questionCountShort ? 'btn-primary' : 'btn-outline-secondary']"
                            @click="selectQuestionCount(questionCountShort)">
                            {{ $Resources.AIAssistantCreateSurveyLengthOptionShort }}
                        </button>
                        <button
                            type="button"
                            :class="['btn', 'btn-sm', selectedQuestionCount === questionCountMedium ? 'btn-primary' : 'btn-outline-secondary']"
                            @click="selectQuestionCount(questionCountMedium)">
                            {{ $Resources.AIAssistantCreateSurveyLengthOptionMedium }}
                        </button>
                        <button
                            type="button"
                            :class="['btn', 'btn-sm', selectedQuestionCount === questionCountLong ? 'btn-primary' : 'btn-outline-secondary']"
                            @click="selectQuestionCount(questionCountLong)">
                            {{ $Resources.AIAssistantCreateSurveyLengthOptionLong }}
                        </button>
                    </div>
                </div>

                <!-- Rating Scale Range Selector -->
                <div class="form-group">
                    <h3 class="label-look">{{ $Resources.AIAssistantCreateSurveyRatingScaleOptionTitle + ':' }}</h3>
                    <div class="d-flex align-items-center">
                        1 {{ $Resources.AIAssistantCreateSurveyRatingScaleRangeTo }} &nbsp;
                        <div class="btn-group">
                            <button
                                v-for="numberOfRatingScoresOption in ratingScaleOptions"
                                :key="numberOfRatingScoresOption"
                                type="button"
                                :class="['btn', 'btn-sm', numberOfRatingScores === numberOfRatingScoresOption ? 'btn-primary' : 'btn-outline-secondary']"
                                @click="selectNumberOfRatingScores(numberOfRatingScoresOption)">
                                {{ numberOfRatingScoresOption }}
                            </button>
                        </div>
                    </div>
                </div>

                <!-- Page Layout Selector -->
                <div class="form-group">
                    <h3 class="label-look">{{ $Resources.AIAssistantCreateSurveyPageLayoutOptionTitle + ':' }}</h3>
                    <div class="btn-group">
                        <button type="button" :class="['btn', 'btn-sm', isOnePage ? 'btn-primary' : 'btn-outline-secondary']" @click="togglePageLayout(true)">
                            {{ $Resources.AIAssistantCreateSurveyPageLayoutOptionSingle }}
                        </button>
                        <button type="button" :class="['btn', 'btn-sm', !isOnePage ? 'btn-primary' : 'btn-outline-secondary']" @click="togglePageLayout(false)">
                            {{ $Resources.AIAssistantCreateSurveyPageLayoutOptionMultiple }}
                        </button>
                    </div>
                </div>

                <div v-if="showConsumption()">
                    <span>{{ getFormattedConsumption() }} % {{ $Resources.AIAssistantConsumptionPostText }}</span>
                </div>
            </div>

            <BsAlert
                v-if="shared.loading && shared.showAIDisclaimer"
                :type="AlertTypes.Info"
                :dismissible="true"
                :content="$Resources.AIAssistantCreateSurveyDisclaimer"
                :buttonText="$Resources.AIAssistantCreateSurveyDisclaimerDismissButton"
                @buttonClick="dismissAIDisclaimer()"></BsAlert>

            <Loading :show="shared.loading" :loadingStyle="'progress-bar'" fixed />
        </template>

        <template #button>
            <button
                type="button"
                class="btn btn-primary p-lg-r p-lg-l"
                :disabled="!isSurveyTypeSelected || state.prompt == '' || shared.loading || !shared.termsAccepted"
                :class="shared.loading && 'is-busy'"
                @click="create">
                {{ $Resources.CreateButton }}
            </button>
        </template>
    </Modal>
</template>
